import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  Briefcase, 
  MapPin, 
  Phone, 
  Mail, 
  Star,
  Building2,
  Award,
  Calendar,
  MessageSquare,
  CheckCircle,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TitleCompanyProfilePage({ user, subscription, onLogout }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const { mainClass } = useSidebar();

  useEffect(() => {
    fetchCompanyProfile();
  }, [companyId]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/title/directory/${companyId}`, {
        withCredentials: true
      });
      setCompany(response.data);
    } catch (error) {
      console.error("Failed to fetch title company:", error);
      toast.error("Title company not found");
      navigate("/title-companies");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!company) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        <div className="p-6 lg:p-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="text-white/50 hover:text-white mb-6"
            onClick={() => navigate("/title-companies")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Title Directory
          </Button>

          {/* Profile Header */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Logo */}
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
                {company.logo_url ? (
                  <img src={company.logo_url} alt={company.company_name} className="w-full h-full rounded-2xl object-cover" />
                ) : (
                  company.company_name?.charAt(0) || "T"
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-white">{company.company_name}</h1>
                    <p className="text-white/50 flex items-center gap-2 mt-1">
                      <Building2 className="w-4 h-4" />
                      {company.contact_name}
                    </p>
                    {company.city && (
                      <p className="text-white/40 flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4" />
                        {company.address || `${company.city}, ${company.state}`}
                      </p>
                    )}
                  </div>
                  {company.rating && (
                    <div className="flex items-center gap-2 bg-gold/20 px-4 py-2 rounded-2xl">
                      <Star className="w-5 h-5 text-gold fill-gold" />
                      <span className="text-gold font-bold text-lg">{company.rating}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 mt-4">
                  {company.orders_completed > 0 && (
                    <div className="flex items-center gap-2 text-white/70">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{company.orders_completed} Orders Completed</span>
                    </div>
                  )}
                  {company.years_in_business && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Calendar className="w-4 h-4 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                      <span>{company.years_in_business} Years in Business</span>
                    </div>
                  )}
                  {company.license_number && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Award className="w-4 h-4 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                      <span>License: {company.license_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Bio */}
              {company.bio && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">About</h2>
                  <p className="text-white/70 leading-relaxed">{company.bio}</p>
                </div>
              )}

              {/* Services */}
              {company.services && company.services.length > 0 && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">Services Offered</h2>
                  <div className="flex flex-wrap gap-2">
                    {company.services.map((service, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Service Areas */}
              {company.service_areas && company.service_areas.length > 0 && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4">Service Areas</h2>
                  <div className="flex flex-wrap gap-2">
                    {company.service_areas.map((area, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee Schedule */}
              {company.fee_schedule && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Fee Schedule
                  </h2>
                  <div className="space-y-3">
                    {company.fee_schedule.title_search && (
                      <div className="flex justify-between items-center py-2 border-b border-white/[0.08]">
                        <span className="text-white/70">Title Search</span>
                        <span className="text-white font-medium">${company.fee_schedule.title_search}</span>
                      </div>
                    )}
                    {company.fee_schedule.closing_fee && (
                      <div className="flex justify-between items-center py-2 border-b border-white/[0.08]">
                        <span className="text-white/70">Closing Fee</span>
                        <span className="text-white font-medium">${company.fee_schedule.closing_fee}</span>
                      </div>
                    )}
                    {company.fee_schedule.title_insurance_rate && (
                      <div className="flex justify-between items-center py-2 border-b border-white/[0.08]">
                        <span className="text-white/70">Title Insurance Rate</span>
                        <span className="text-white font-medium">{company.fee_schedule.title_insurance_rate}%</span>
                      </div>
                    )}
                    {company.fee_schedule.recording_fee && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white/70">Recording Fee</span>
                        <span className="text-white font-medium">${company.fee_schedule.recording_fee}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <h2 className="text-lg font-bold text-white mb-4">Contact</h2>
                <div className="space-y-3">
                  {company.phone && (
                    <Button 
                      className="w-full justify-start bg-white/[0.08] hover:bg-white/[0.1] text-white"
                      onClick={() => window.open(`tel:${company.phone}`)}
                    >
                      <Phone className="w-4 h-4 mr-3" />
                      {company.phone}
                    </Button>
                  )}
                  {company.email && (
                    <Button 
                      className="w-full justify-start bg-white/[0.08] hover:bg-white/[0.1] text-white"
                      onClick={() => window.open(`mailto:${company.email}`)}
                    >
                      <Mail className="w-4 h-4 mr-3" />
                      {company.email}
                    </Button>
                  )}
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white mt-4"
                    onClick={() => toast.info("Request quote feature coming soon!")}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Quote
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
